import React from 'react';

import cn from 'classnames';
import { isFunction } from 'lodash';

import {
  bool,
  element,
  func,
  oneOf,
  oneOfType,
  string,
  number,
  shape,
} from 'prop-types';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';

import { getColor } from 'utils/colors';

import s from './FontAwesomeIcon.css';

/**
 * Wrapper for using Font Awesome icons.
 * https://fontawesome.com/docs/web/use-with/react/style
 *
 * @param {bool} border - adds a border around the icon
 * @param {string} className - used for additional styling
 * @param {string} color - color of icon
 * @param {bool} fixedWidth - sets icon to fixed width. helpful for aligning icons in a column.
 * @param {string} flip - flips icon horizontally, vertically, or both
 * @param {number} height - height of the icon
 * @param {string | element} icon - the main icon to render
 * @param {bool} inverse - invert icon color to white
 * @param {bool} listItem - can replace bullet point in a list
 * @param {string | element} mask - cuts out icon shape from main icon
 * @param {func} onClick - handles onClick event of the icon
 * @param {string} pull - wrap text around icon
 * @param {number} rotation - can rotate in 90 degree increments
 * @param {string} size - sets the font size. use t-shirt sizing (xs, ls or sm), x-factor sizing (1x-10x) or set custom font-size via className
 * @param {bool} spin - adds 360 degree spinning animation to the icon
 * @param {bool} swapOpacity - swaps opacity on duotone icons
 * @param {number} width - width of the icon
 * @param {number} x - x axis coordinate of icon
 * @param {number} y - y axis coordinate of icon
 *
 * t-shirt sizing map:
 * fa-xs=0.75em=12px
 * fa-sm=0.875em=14px
 * fa-lg=1.25em=20px
 */
const FontAwesomeIcon = ({
  border,
  className,
  color,
  fixedWidth,
  flip,
  height,
  icon,
  inverse,
  listItem,
  mask,
  onClick,
  pull,
  rotation,
  size,
  spin,
  swapOpacity,
  width,
  x,
  y,
  ...restProps
}) => (
  <FA
    border={border}
    className={cn(className, {
      [s.pointer]: isFunction(onClick),
    })}
    color={getColor({ color, useFallback: false }) || color}
    fixedWidth={fixedWidth}
    flip={flip}
    icon={icon}
    height={height}
    inverse={inverse}
    listItem={listItem}
    mask={mask}
    onClick={onClick}
    pull={pull}
    rotation={rotation}
    size={size}
    spin={spin}
    swapOpacity={swapOpacity}
    width={width}
    x={x}
    y={y}
    {...restProps}
  />
);

export default FontAwesomeIcon;

FontAwesomeIcon.propTypes = {
  border: bool,
  className: string,
  color: string,
  fixedWidth: bool,
  flip: oneOf(['horizontal', 'vertical', 'both']),
  height: number,
  icon: oneOfType([element, string, shape({})]).isRequired,
  inverse: bool,
  listItem: bool,
  mask: oneOfType([element, string]),
  onClick: func,
  pull: oneOf(['left', 'right']),
  rotation: oneOf([0, 90, 180, 270]),
  size: oneOf([
    'xs',
    'lg',
    'sm',
    '1x',
    '2x',
    '3x',
    '4x',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x',
  ]),
  spin: bool,
  swapOpacity: bool,
  width: number,
  x: number,
  y: number,
};

FontAwesomeIcon.defaultProps = {
  border: undefined,
  className: undefined,
  color: undefined,
  fixedWidth: false,
  flip: undefined,
  height: undefined,
  inverse: false,
  listItem: false,
  mask: undefined,
  onClick: undefined,
  pull: undefined,
  rotation: undefined,
  size: undefined,
  spin: false,
  swapOpacity: false,
  width: undefined,
  x: undefined,
  y: undefined,
};
